import React, { Fragment, FunctionComponent, useEffect, useRef, useState } from 'react';
import {
    Col,
    Row as BRow,
    Table,
    Form,
    DropdownButton,
    Dropdown,
} from 'react-bootstrap';

import { Cell, Row } from '../../../utils';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import { AuthedLayout } from '../../../components';
import { useDownloadSheetChart, useSemGrade } from '../../../hook';
import { DiseaseChartStatistic } from '../../../model';
import apis from '../../../apis';
import useApexBarChart, { ChartState } from '../../../hook/useApexBarChart';
import { toast } from 'react-toastify';

const borderStyle = { color: { rgb: '000000' }, style: 'thin' } as const;
const fullBorderStyle = {
    top: borderStyle,
    bottom: borderStyle,
    left: borderStyle,
    right: borderStyle,
} as const;
const cellAlignStyle = {
    horizontal: 'center',
    vertical: 'center',
} as const;

const mapState = (state: ApplicationState) => ({ ...state.auth });
const mapDispatches = ErrorDispatches;

const connector = connect(mapState, mapDispatches);

type Props = ConnectedProps<typeof connector>;

type DiseaseSummary = {
    diseaseId: number;
    diseaseName: string;
    gradeCounts: Record<number, number>;
    total: number;
};

const diseaseStaticChart: FunctionComponent<Props> = ({
    catchErrorForModal,
}) => {
    const { yearSem, element: semGradeElement } = useSemGrade();
    const [students, setStudents] = useState<DiseaseChartStatistic[]>([]);
    const chartRef = useRef<HTMLDivElement>(null);
    const [onlySchoolCreated, setOnlySchoolCreated] = useState<boolean>(true);
    useEffect(() => {
        if (yearSem) {
            toast
                .promise(
                    apis.getDiseaseStaticChart(yearSem.year, yearSem.sem, onlySchoolCreated)
                        .then((r) =>
                            setStudents(
                                r.map(({ ...s }) => ({
                                    ...s,
                                }))
                            )
                        ),
                    {
                        pending: '資料載入中......',
                        success: '資料載入成功！',
                        error: '資料載入失敗！請查看錯誤資訊。',
                    }
                )
        }
    }, [yearSem?.year, yearSem?.sem, onlySchoolCreated]);

    let gradearr = students.flatMap((gs, idx, allArray) => {
        return gs.grade
    })
    gradearr = gradearr.filter((value, index, array) => array.indexOf(value) === index)
    const gradeRow = gradearr.flatMap((gs, idx, allArray) => {
        return {
            value: gs,
            merge: { column: 1 },
            style: { border: fullBorderStyle, width: "5%" },
        }
    })

    const diseasearr: string[] = []
    const schooltotal: DiseaseSummary = {
        diseaseId: 0,
        diseaseName: '總計',
        gradeCounts: {},
        total: 0
    };

    gradearr.forEach((gs) => {
        schooltotal.gradeCounts[gs] = 0;
    });

    const calculateDiseaseSummary = (data: DiseaseChartStatistic[]): Record<string, DiseaseSummary> => {
        const summary: Record<string, DiseaseSummary> = {};

        data.forEach(item => {
            if (!summary[item.diseaseName]) {
                diseasearr.push(item.diseaseName);
                summary[item.diseaseName] = {
                    diseaseId: item.diseaseId,
                    diseaseName: item.diseaseName,
                    gradeCounts: {},
                    total: 0
                };
                gradearr.forEach((gs) => {
                    summary[item.diseaseName].gradeCounts[gs] = 0;
                });
            }

            summary[item.diseaseName].gradeCounts[item.grade] += item.count;
            summary[item.diseaseName].total += item.count;

            schooltotal.gradeCounts[item.grade] += item.count;
            schooltotal.total += item.count;
        });

        return summary;
    };

    const diseaseSummary = calculateDiseaseSummary(students);


    const contentRows: Row[] = Object.values(diseaseSummary).flatMap(
        (gradedata, gidx, allArray) => {
            return DiseaseToRows(
                gradedata.diseaseName,
                gradedata.gradeCounts,
                gradedata.total
            )
        }
    );

    const content: Row[] = [
        {
            cells: [
                {
                    value: '疾病種類/人數/年級',
                    merge: { column: 1 },
                    style: { border: fullBorderStyle },
                },
                ...gradeRow,
                {
                    value: '總計',
                    merge: { column: 1 },
                    style: { border: fullBorderStyle },
                },
            ],
        },
        ...contentRows,
        ...DiseaseToRows(schooltotal.diseaseName, schooltotal.gradeCounts, schooltotal.total)
    ];
    const state: ChartState = {
        options: {
            chart: {
                id: 'diseasechart'
            },
            xaxis: {
                title: { text: "疾病" },
                categories: diseasearr
            },
            yaxis: {
                title: { text: "人數" },
            },
        },
        series: [
            {
                name: '',
                data: Object.values(diseaseSummary).flatMap(x => x.total)
            }],
        charttype: 'bar'
    }
    const { element: barchart } = useApexBarChart(state);
    const chartRefs = [
        {
            ref: 'diseasechart',
            sheetname: '統計圖',
        },
    ];

    const titles = [
        `${yearSem?.year}學年第${yearSem?.sem}學期_特殊疾病統計`,
    ];
    const {
        downloadChart,
        disabled: exportDisabled,
    } = useDownloadSheetChart({
        chartdata: Object.values(diseaseSummary).flatMap(
            x => x.total
        ),
        chartRefs,
        titles,
        tablesheets: content,
        tablesheetname: '特殊疾病統計',
    });

    const handlePrint = () => {
        const chartElement = chartRef.current as HTMLElement | null;

        if (!chartElement) {
            return;
        }

        const printWindow = window.open(
            '',
            '_blank',
            'width=1200,height=900'
        );

        if (!printWindow) {
            alert('瀏覽器阻擋了列印視窗，請允許彈出式視窗。');
            return;
        }

        const escapeHtml = (value: unknown): string => {
            return String(value ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
        };

        const title =
            `${yearSem?.year}學年第${yearSem?.sem}學期_特殊疾病統計`;

        const tableRows = content
            .map((row) => {
                const cells = row.cells
                    .map((cell) => {
                        if (cell.value === undefined) {
                            return '';
                        }

                        const colSpan = cell.merge?.column
                            ? ` colspan="${cell.merge.column}"`
                            : '';

                        const rowSpan = cell.merge?.row
                            ? ` rowspan="${cell.merge.row}"`
                            : '';

                        return `
                        <td${colSpan}${rowSpan}>
                            ${escapeHtml(cell.value)}
                        </td>
                    `;
                    })
                    .join('');

                return `<tr>${cells}</tr>`;
            })
            .join('');

        printWindow.document.open();

        printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="zh-Hant">
        <head>
            <meta charset="UTF-8" />

            <title>${escapeHtml(title)}</title>

            <style>
                * {
                    box-sizing: border-box;
                }

                html,
                body {
                    margin: 0;
                    padding: 0;
                    font-family:
                        "Microsoft JhengHei",
                        "Noto Sans TC",
                        Arial,
                        sans-serif;
                    color: #000;
                }

                .print-page {
                    min-height: 190mm;
                    display: flex;
                    flex-direction: column;
                }

                h1 {
                    margin: 0 0 15px;
                    font-size: 18px;
                    text-align: center;
                }

                table {
                    width: 100%;
                    border-collapse: collapse;
                    table-layout: fixed;
                    margin-bottom: 20px;
                }

                td {
                    border: 1px solid #000;
                    padding: 5px 3px;
                    text-align: center;
                    vertical-align: middle;
                    font-size: 11px;
                    word-break: break-word;
                }

                .chart-container {
                    width: 100%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }

                .chart-container svg {
                    max-width: 100% !important;
                    height: auto !important;
                }

                .footer {
                    margin-top: auto;
                    width: 100%;
                    padding-top: 20px;
                    display: flex;
                    justify-content: space-between;
                    font-size: 12px;
                }

                @page {
                    size: A4 landscape;
                    margin: 8mm;
                }

                @media print {
                    body {
                        padding: 0;
                    }

                    tr {
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                }
            </style>
        </head>

        <body>
            <div class="print-page">

                <h1>${escapeHtml(title)}</h1>

                <table>
                    <tbody>
                        ${tableRows}
                    </tbody>
                </table>

                <div class="chart-container">
                    ${chartElement.innerHTML}
                </div>

                <div class="footer">
                    <span>承辦人:</span>
                    <span>組長:</span>
                    <span>主任:</span>
                    <span>校長:</span>
                </div>

            </div>

            <script>
                window.onload = function () {
                    window.focus();

                    setTimeout(function () {
                        window.print();
                    }, 300);
                };

                window.onafterprint = function () {
                    window.close();
                };
            </script>

        </body>
        </html>
    `);

        printWindow.document.close();
    };

    return (
        <AuthedLayout>
            <BRow className="justify-content-between">
                <Col xs={3} className="mr-3">
                    {semGradeElement}
                </Col>
                <Col xs={3} className="mr-3">
                    <Form.Check
                        type="checkbox"
                        id="chk-only-school-created"
                        label="只顯示本校建置資料"
                        checked={onlySchoolCreated}
                        onChange={(e) => setOnlySchoolCreated(e.target.checked)}
                    />
                </Col>
                <Col xs={2} className="mr-2">
                    <DropdownButton
                        id="disease-static-export"
                        title="匯出"
                        variant="success"
                        disabled={exportDisabled}
                    >
                        <Dropdown.Item onClick={downloadChart}>
                            Excel 下載
                        </Dropdown.Item>

                        <Dropdown.Item onClick={handlePrint}>
                            列印
                        </Dropdown.Item>
                    </DropdownButton>
                </Col>
            </BRow>
            <hr />
            <BRow>
                <Col className="text-center">
                    {yearSem?.year}學年第{yearSem?.sem}學期_特殊疾病統計
                </Col>
            </BRow>
            <BRow>
                <Table
                    striped
                    bordered
                    className="text-center"
                    style={{ tableLayout: 'fixed' }}
                >
                    <tbody>
                        {content.map((r, i) => {
                            const cells = r.cells.map((c, j) =>
                                c.value != undefined ? (
                                    <td
                                        colSpan={c.merge?.column}
                                        rowSpan={c.merge?.row}
                                        key={`${i}-${j}`}
                                    >
                                        {c.value}
                                    </td>
                                ) : (
                                    <Fragment key={`${i}-${j}`}></Fragment>
                                )
                            );
                            return <tr key={i}>{cells}</tr>;
                        })}
                    </tbody>
                </Table>
            </BRow>
            <BRow className="justify-content-center">
                <div ref={chartRef}>
                    {barchart}
                </div>
            </BRow>
        </AuthedLayout >
    );
};

function DiseaseToRows(
    diseaseName: string,
    gradedata: { [key: string]: number; },
    total: number,
    gradeMerge?: { row?: number; column?: number }
): Row[] {
    const gradeNo = [
        {
            value: diseaseName,
            merge: gradeMerge,
            style: { border: fullBorderStyle },
        },
    ];

    const gradedatarow = Object.values(gradedata).flatMap((data, idx, allArray) => {
        return {
            value: data ?? 0,
            merge: {},
            style: { border: { left: borderStyle } },
        }
    })

    return [
        {
            cells: [
                ...gradeNo,
                ...gradedatarow,
                {
                    value: total,
                    merge: {},
                    style: { border: fullBorderStyle },
                },
            ],
        },
    ];
}


export const DiseaseStaticChart = connector(diseaseStaticChart);
